import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ShipAssureLogo from '../common/ShipAssureLogo';

const PLANS = [
  { credits: 250,  amount: 500,  label: 'Starter', ratePerVideo: 2.0, popular: false },
  { credits: 1250, amount: 2000, label: 'Growth',  ratePerVideo: 1.6, popular: true  },
  { credits: 5000, amount: 5000, label: 'Pro',     ratePerVideo: 1.0, popular: false },
];

const RAZORPAY_KEY = import.meta.env.VITE_RAZORPAY_KEY;

const RechargeModal = ({ isOpen, onClose, onSuccess, userEmail }) => {
  const [selected, setSelected] = useState(PLANS[1]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload  = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePay = async () => {
    setError('');
    setLoading(true);

    if (!RAZORPAY_KEY) {
      setError('Payment gateway is not configured. Please contact support.');
      setLoading(false);
      return;
    }

    const loaded = await loadRazorpay();
    if (!loaded) {
      setError('Failed to load payment gateway. Check your internet connection.');
      setLoading(false);
      return;
    }

    const plan = selected;

    const options = {
      key:         RAZORPAY_KEY,
      amount:      plan.amount * 100,
      currency:    'INR',
      name:        'ShipAssure',
      description: `${plan.credits} Recording Credits — ${plan.label} Plan`,
      prefill:     { email: userEmail || '' },
      theme:       { color: '#2563eb' },
      handler: (response) => {
        onSuccess(plan.credits, response.razorpay_payment_id, plan.label, plan.amount);
        setLoading(false);
        onClose();
      },
      modal: { ondismiss: () => setLoading(false) },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setError(`Payment failed: ${resp.error.description}`);
        setLoading(false);
      });
      rzp.open();
    } catch {
      setError('Could not open payment window.');
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1,    y: 0  }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShipAssureLogo size={34} />
                  <div>
                    <h2 className="text-xl font-bold">Recharge Credits</h2>
                    <p className="text-blue-100 text-sm mt-0.5">Choose a plan to continue recording</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <SafeIcon icon={FiIcons.FiX} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiAlertCircle} className="flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Credits never expire notice */}
              <div className="bg-green-50 border border-green-200 text-green-700 text-xs px-3 py-2 rounded-lg flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="flex-shrink-0 text-green-500" />
                <span><strong>Credits never expire</strong> — use them at your own pace, anytime.</span>
              </div>

              <div className="space-y-3">
                {PLANS.map((plan) => (
                  <button
                    key={plan.label}
                    onClick={() => setSelected(plan)}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all relative ${
                      selected.label === plan.label
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-2.5 right-3 text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-bold">
                        Popular
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-gray-800">{plan.label}</p>
                        <p className="text-sm text-gray-500">{plan.credits.toLocaleString()} recordings</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">₹{plan.amount.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">₹{plan.ratePerVideo.toFixed(1)} / video</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={handlePay}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-200"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <SafeIcon icon={FiIcons.FiCreditCard} />
                    Pay ₹{selected.amount.toLocaleString()} · Get {selected.credits.toLocaleString()} Credits
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
                <SafeIcon icon={FiIcons.FiLock} /> Secured by Razorpay
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RechargeModal;